import type { CombinedError, Operation } from "@urql/vue";
import { useConflictRevisions } from "@/composables/conflict/revisions";
import { useLogger } from "@/composables/logger";
import { captureException } from "@sentry/vue";
import type { DefinitionNode } from "graphql";

type DefinitionWithName<T> = T & { name: { kind: string; value: string } };

const { warn: logWarning } = useLogger("URQL_ERROR_EXCHANGE");
const { insertOrUpdateRevisionConflict } = useConflictRevisions({});
const isProd = import.meta.env.MODE === "production";
class GraphQLError extends Error {
  constructor(message: string, name: string) {
    super(message);
    this.name = name;
  }
}

export const onError: (error: CombinedError, operation: Operation) => void = (
  error,
  operation
) => {
  const errorIgnoreList = [
    "[GraphQL] Could not verify JWT: JWTExpired", // Auth exchange retries request after updating token.
    "[Network] Failed to fetch" // User is offline. Will continue to work.
  ];

  if (errorIgnoreList.includes(error.message)) return;

  if (isProd) {
    const kind = operation.kind;
    const query = operation.query
      .definitions[0] as DefinitionWithName<DefinitionNode>;
    const message = error.message;

    captureException(
      new GraphQLError(
        `${query.name.value} ${kind} error: ${message}`,
        `[GraphQL] ${query.name.value} ${kind}`
      ),
      {
        extra: {
          name: query.name.value,
          kind,
          variables: JSON.stringify(operation.variables),
          skErrors: error.graphQLErrors
            .filter(
              graphQLError =>
                graphQLError.extensions?.code === "sk_request_failed"
            )
            .map(graphQLError => ({
              upstreamStatus: graphQLError.extensions.upstream_status,
              details: graphQLError.extensions.details
            }))
          // TODO: change Sentry settings to allow this -> source: operation.query.loc?.source.body
        }
      }
    );
  } else {
    logWarning({ error, operation });
  }

  const conflictError = (
    error.graphQLErrors[0]?.extensions?.internal as {
      error: { status_code: string };
    }
  )?.error;

  if (conflictError?.status_code === "RC001") {
    insertOrUpdateRevisionConflict(operation, error);
  }
};
